<script setup>
import { ref, onMounted, inject } from 'vue'
import { fetchWrapper, getBaseUrl } from '@/helpers/fetch-wrapper'
import { useI18n } from 'vue-i18n'
import { useProfileStore } from '@/stores/profile.store'
import { useAuthStore } from '@/stores/auth.store'
import { Marked } from 'marked'
const marked = new Marked({
  gfm: true,
  breaks: true,
})

const props = defineProps({
  curriculum_conceptID: {
    type: [Number, String],
    required: false,
  },
  vocab_entryID: {
    type: [Number, String],
    required: false,
  },
  game_sessionID: {
    type: [Number, String],
    required: false,
  },
})

const { t } = useI18n()
const profileStore = useProfileStore()
const authStore = useAuthStore()
const exampleSentences = ref([])
const isLoading = ref(true)
const conceptInfo = ref(null)
const audiomanager = inject('audiomanager')

async function loadExampleSentences(prepend = false) {
  try {
    const payload = {
      learning_language: profileStore.profile.learning_language,
      native_language: profileStore.profile.native_language,
      existing_sentences: exampleSentences.value.map(sentence => sentence.text),
      vocab_entryID: props.vocab_entryID,
      curriculum_conceptID: props.curriculum_conceptID,
      game_sessionID: props.game_sessionID,
    }
    const response = await fetchWrapper.post(`/curriculum/example-sentences/`, payload)
    
    // Store concept info from the first response
    if (!prepend && response.length > 0) {
      conceptInfo.value = {
        concept_name: response[0].concept.text,
        concept_translation: response[0].concept.translation,
      }
    }
    
    for (const sentence of response) {
      sentence.show_breakdown = false
      sentence.show_translation = false
      sentence.breakdown = marked.parse(sentence.breakdown)
    }
    if (prepend) {
      exampleSentences.value = [...response, ...exampleSentences.value]
    } else {
      exampleSentences.value = response
    }
  } catch (error) {
    console.error('Error loading example sentences:', error)
  } finally {
    isLoading.value = false
  }
}

function playAudio(sentence) {
  const user = authStore.user
  const url = getBaseUrl(`/tts/?lang=${profileStore.profile.learning_language}&text=${sentence.text}&token=${user.token}`)
  audiomanager.setPlaybackSpeed(1.0)
  audiomanager.play(url)
  sentence.showControls = false
}

function playAudioSlow(sentence) {
  const user = authStore.user
  const url = getBaseUrl(`/tts/?lang=${profileStore.profile.learning_language}&text=${sentence.text}&token=${user.token}`)
  audiomanager.setPlaybackSpeed(0.5)
  audiomanager.play(url)
  sentence.showControls = false
}

function toggleBreakdown(sentence) {
  sentence.show_breakdown = !sentence.show_breakdown
  sentence.showControls = false
}

function toggleTranslation(sentence) {
  sentence.show_translation = !sentence.show_translation
  sentence.showControls = false
}

onMounted(async () => {
  await profileStore.fetchProfile()
  await loadExampleSentences()
})
</script>

<template>
  <div>
    <loading-overlay v-model:active="isLoading" :can-cancel="true" is_full_page="false" loader="dots" :opacity="0.8" />
    <div v-if="!isLoading">
      <div v-if="conceptInfo" class="mb-3">
        <h4 class="text-center">
          <span v-if="conceptInfo.vocab_word">
            Examples for: "<strong>{{ conceptInfo.vocab_word }}</strong>"
            <span v-if="conceptInfo.vocab_translation" class="translation-text"> ({{ conceptInfo.vocab_translation }})</span>
          </span>
          <span v-else-if="conceptInfo.concept_name">
            Examples for: "<strong>{{ conceptInfo.concept_name }}</strong>"
            <span v-if="conceptInfo.concept_translation" class="translation-text"> ({{ conceptInfo.concept_translation }})</span>
          </span>
          <span v-else>{{ t('curriculum.example-sentences') }}</span>
        </h4>
        <hr>
      </div>
      <div class="row">
        <div v-for="sentence in exampleSentences" :key="sentence.id" class="col-md-12 mb-3">
          <div class="card">
            <div class="card-body">
              <div class="row">
                <div class="col-10">
                  <p class="card-text clickable-sentence" @click="toggleTranslation(sentence)" v-html="sentence.annotation" />
                  <p v-if="sentence.show_translation" class="card-text translation-text">{{ sentence.translation }}</p>
                  <div v-if="sentence.show_breakdown" class="mt-2">
                    <p class="card-text"><strong>Breakdown:</strong></p> 
                    <p class="breakdown-text" v-html="sentence.breakdown" />
                  </div>
                </div>
                <div class="col-2 align-items-center justify-content-end">
                  <button
                    class="btn btn-outline-secondary me-1"
                    @click="sentence.showControls = !sentence.showControls">
                    <i :class="sentence.showControls ? 'bi bi-gear' : 'bi bi-gear'"></i>
                  </button>
                  <div v-if="sentence.showControls" class="popout-menu">
                    <ul class="text-start">
                      <li @click="playAudio(sentence)" >
                        <i class="bi bi-play-circle me-1"></i>
                        {{ t('curriculum.play-again') }}
                      </li>
                      <li @click="playAudioSlow(sentence)" >
                        <i class="bi bi-play-circle me-1"></i>
                        {{ t('curriculum.play-slow') }}
                      </li>
                      <li @click="toggleBreakdown(sentence)">
                        <i class="bi bi-question-circle me-1"></i>
                        {{ t('chat.explain-this-sentence') }}
                      </li>
                      <li @click="toggleTranslation(sentence)">
                        <i class="bi bi-translate me-1"></i>
                        {{ sentence.show_translation ? t('chat.hide-translation') : t('chat.show-translation') }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button @click="loadExampleSentences(true)" class="btn btn-primary btn-primary-cs mb-3">{{ t('curriculum.load-more-sentences')
        }}</button>
    </div>
  </div>
</template>

<style scoped>
/* Add your styles here */
/* Styles for the popout menu */
.popout-menu {
    position: fixed;
    background: white;
    border: 1px solid #ccc;
    border-radius: 5px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    transform: translateX(-80%);
    width: 250px;
    z-index: 1000;
}

.popout-menu ul {
    list-style: none;
    margin: 0;
    padding: 0;
}

.popout-menu li {
    padding: 5px;
    cursor: pointer;
    display: flex;
}

.popout-menu li:hover {
    background-color: #f1f1f1;
}

.translation-text {
    font-style: italic;
    font-size: 0.85em;
    color: #17a2b8;
    font-weight: normal;
}

.clickable-sentence {
    cursor: pointer;
}

.clickable-sentence:hover {
    background-color: #f8f9fa;
}

</style>
<style>
.breakdown-text p {
    color: #28a745 !important;
}
</style>